import type { AllTextDefaultStylesType } from '../grid/reduxStore/textDefaultStylingSlice';
import { TableDefaultClassNamesEnum } from '../grid/reduxStore/textDefaultStylingSlice';
import { TableRowDefaultClassNamesEnum } from '../grid/reduxStore/tableRowDefaultStyleSlice.ts';

type TableStylesResult = {
  textBlockTableStyles: Record<string, any>;
  numberBlockStyles: Record<string, any>;
};

type GridRowConfig = {
  tiptapSelector: string;
  rowClassName: TableRowDefaultClassNamesEnum;
  styleSelector: TableDefaultClassNamesEnum;
};

// Maps each table text style (body/header/footer/subtotal/total) to the row
// wrapper class the DataGrid already renders for that row type. Tax and
// Discount rows share the Subtotal style, matching TableRowDefaultClassNamesEnum.
const STYLE_TO_ROW_CLASS: Record<TableDefaultClassNamesEnum, TableRowDefaultClassNamesEnum> = {
  [TableDefaultClassNamesEnum.TABLE_BODY]: TableRowDefaultClassNamesEnum.BODY,
  [TableDefaultClassNamesEnum.TABLE_HEADER]: TableRowDefaultClassNamesEnum.HEADER,
  [TableDefaultClassNamesEnum.TABLE_FOOTER]: TableRowDefaultClassNamesEnum.FOOTER,
  [TableDefaultClassNamesEnum.TABLE_SUBTOTAL]: TableRowDefaultClassNamesEnum.SUBTOTAL,
  [TableDefaultClassNamesEnum.TABLE_TOTAL]: TableRowDefaultClassNamesEnum.TOTAL,
};

export function mapTableStylesToAllBlocks(
  tableTextDefaultStyles: Record<TableDefaultClassNamesEnum, AllTextDefaultStylesType>
): TableStylesResult {
  const GRID_ROW_CONFIGS: GridRowConfig[] = Object.values(TableDefaultClassNamesEnum).map((value) => ({
    tiptapSelector: `${value}`,
    rowClassName: STYLE_TO_ROW_CLASS[value],
    styleSelector: value,
  }));

  return GRID_ROW_CONFIGS.reduce(
    (acc, { tiptapSelector, rowClassName, styleSelector }) => {
      const dataGridTableStyles = tableTextDefaultStyles[styleSelector];
      // Numeric cells (grid cells and footer row values) manage their own alignment
      // already, so textAlign is excluded to avoid overriding it. textDecoration is
      // excluded too, since underline/strikethrough aren't wanted on numeric values.
      const { textAlign: _textAlign, textDecoration: _textDecoration, ...numberBlockTextStyles } = dataGridTableStyles ?? {};

      return {
        textBlockTableStyles: {
          ...acc.textBlockTableStyles,
          [`p[data-table-style="${tiptapSelector}"]`]: dataGridTableStyles,
        },
        numberBlockStyles: {
          ...acc.numberBlockStyles,
          [`.${rowClassName} .py-numeric-cell-value`]: numberBlockTextStyles,
        },
      };
    },
    { textBlockTableStyles: {}, numberBlockStyles: {} }
  );
}
